import React, { useState } from "react";
import { Container } from "reactstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { Tag } from "antd";
import { TableC } from "../../../CommonElements/TableC";
import { useHousingPermitsQuery } from "../../../services/housing";
import { useDebouncedSearch } from "../../../utils/hooks/useDebouncedSearch";
import { Link } from "react-router";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon, Edit02Icon } from "@hugeicons/core-free-icons";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";
import { MAKKAH_PERMISSIONS } from "@/utils/constants";
import usePermissions from "@/utils/hooks/usePermissions";

const HousingPermits = () => {
  // ======
  // hooks
  // ======
  const { getLocalizedNumber, getLocalizedText } = useLocalizedText();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const { city_id, currentSystem, from_id } = useSystem();
  const [filters, setFilters] = useState({
    houseCityName: city_id,
  });
  const [page, setPage] = useState(1);

  const isMadina = currentSystem === "madina";

  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  // ======
  // Query
  // ======
  const { data, isLoading, isFetching } = useHousingPermitsQuery({
    search: debouncedValue,
    page: page,
    per_page: 10,
    ...filters,
  });

  const permits = data?.data.data ?? [];
  const meta = data?.data.meta ?? {};



  const columns = [
    {
      name: t("BUILDING_NUMBER"),
      key: "BuildingNo",
    },
    {
      name: t("BUILDING_ZONE"),
      key: "AreaNo",
    },
    {
      name: t("BUILDING_PERMIT_NUMBER"),
      key: "houseComrRegNo",
    },
    {
      name: t("BUILDING_CODE"),
      key: "id",
    },
    {
      name: t("BUILDING_DELEGATE"),
      key: "OwnerName",
    },
    {
      name: t("BUILDING_CITY"),
      key: "houseCityName",
      defaultValue: city_id,
    },
    {
      name: t("BUILDING_STATUS"),
      key: "IsEmpty",
      render: (value) => (
        <Tag color={value == "0" ? "red" : "green"}>
          {value == "0" ? t("BUILDING_NOT_EMPTY") : t("BUILDING_EMPTY")}
        </Tag>
      ),
      visible: !isMadina,
    },

    {
      name: t("ACTUAL_PILGRIMS_HOUSED"),
      key: "numberOfAccommodatedPilgrims",
    },
    {
      name: t("ACTUAL_PILGRIMS_DEPARTED"),
      key: "numberOfDepartedPilgrims",
      visible: !isMadina,
    },
    {
      name: t("ACTUAL_PILGRIMS_REMAINING"),
      key: "remainingPilgrims",
      visible: !isMadina,
    },
    {
      name: t("TOTAL_PILGRIMS"),
      key: "numberOfPilgrims",
      visible: false,
    },
    hasPermission(from_id, MAKKAH_PERMISSIONS.view_housing_details) && {
      name: t("ACTIONS"),
      key: "actions",
    },
  ].filter(Boolean) ;
  const filterFields = [
    {
      name: "BuildingNo",
      label: t("BUILDING_NUMBER"),
      type: "input",
    },
    {
      name: "AreaNo",
      label: t("BUILDING_ZONE"),
      type: "input",
    },
    {
      name: "building_code",
      label: t("BUILDING_CODE"),
      type: "input",
    },
    {
      name: "houseComrRegNo",
      label: t("BUILDING_PERMIT_NUMBER"),
      type: "input",
    },
    {
      name: "building_contract_number",
      label: t("BUILDING_CONTRACT_NUMBER"),
      type: "select",
      placeholder: t("BUILDING_CONTRACT_NUMBER"),
      optionKey: "contracts",
      options: (contract) => ({
        label: getLocalizedNumber(
          [contract.id, contract.house_commercial_name_ar].join(" - ")
        ),
        value: contract.id,
      }),
    },
    {
      name: "IsEmpty",
      label: t("BUILDING_STATUS"),
      type: "select",
      placeholder: t("BUILDING_STATUS"),
      options: [
        { label: t("BUILDING_EMPTY"), value: "1" },
        { label: t("BUILDING_NOT_EMPTY"), value: "0" },
      ],
    },
    {
      name: "OwnerName",
      label: t("BUILDING_DELEGATE"),
      type: "input",
    },
    {
      name: "mission_id",
      label: t("DELEGATION"),
      type: "select",
      placeholder: t("DELEGATION"),
      optionKey: "missions",
      options: (mission) => ({
        label: getLocalizedText(mission.name_ar, mission.name_en),
        value: mission.id,
      }),
    },
    {
      name: "building_organizer",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      placeholder: t("AFFILIATED_ORGANIZER"),
      optionKey: "companies",
      options: (company) => ({
        label: getLocalizedText(company.name_ar, company.name_en),
        value: company.id,
      }),
    },
  ];

  const actions = (rowData) => [
    hasPermission(from_id, MAKKAH_PERMISSIONS.view_housing_details) && {
      key: "view",
      label: (
        <Link
          to={`/app/${currentSystem}/housing-permits/${rowData.id}`}
          className="d-flex align-items-center gap-2"
        >
          <HugeiconsIcon size={16} icon={ViewIcon} />
          {t("VIEW")}
        </Link>
      ),
    },
    {
      type: "divider",
    },
    hasPermission(from_id, MAKKAH_PERMISSIONS.edit_housing_data) && {
      key: "edit",
      label: (
        <Link
          to={`/app/${currentSystem}/housing-permits/${rowData.id}/edit`}
          className="d-flex align-items-center gap-2"
        >
          <HugeiconsIcon size={16} icon={Edit02Icon} />
          {t("EDIT")}
        </Link>
      ),
    },
  ].filter(Boolean);

  return (
    <div>
      <PageHeader title={t("HOUSING_PERMITS")} sticky={false} />
      <Container fluid>
        <TableC
          searchValue={searchValue}
          onSearchChange={(value) => setSearchValue(value.target.value)}
          searchPlaceholder={t("SEARCH_HOUSING_PERMITS")}
          data={
            permits
          }
          columns={columns}
          loading={isLoading || isFetching}
          actions={actions}
          // filter
          filterFields={filterFields}
          filters={filters}
          onFilterApply={({city_id: _, ...filters}) => setFilters({city_id, ...filters})}
          onFilterReset={() => setFilters({})}
          // pagination
          pagination={
            {
              current: page,
              total: meta.total,
              pageSize: meta.per_page,
              onChange: (page) => setPage(page),
            }
          }
        />
      </Container>
    </div>
  );
};

export default HousingPermits;
